import type { LocalizedValue } from '../components/atoms/LocalizedInput/LocalizedInputAtom';
import type { ContextViewRecord, CreateContextViewPayload, UpdateContextViewPayload } from '../api/contextApi';
import type { BaseViewConfig } from 'types/contextView';
import type {
  ContextViewFormState,
  TreeMapConfigFormState,
  BaseConfigFormState,
} from '../components/organisms/ContextViewEditor/ContextViewEditorOrganism';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replaceAll(/[^a-z0-9\s-]/g, '')
    .trim()
    .replaceAll(/\s+/g, '-')
    .replaceAll(/-+/g, '-');
}

/**
 * Generate a unique system_name from a localized name.
 * Prefers the en-US translation; falls back to the default locale.
 * Appends a numeric suffix if the base slug is already in `existing`.
 */
export function generateViewSystemName(
  name: LocalizedValue,
  existing: string[] = []
): string {
  const source = name.translations?.['en-US'] ?? name.default;
  const base = slugify(source) || 'view';
  if (!existing.includes(base)) return base;
  let i = 1;
  while (existing.includes(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

/**
 * Seed a new ContextViewFormState from the TreeMap's current merged rendering.
 * Always locks viewType to 'treemap' and emits the type discriminator on viewConfig.
 */
export function currentRenderingToViewForm(
  mergedViewConfig: Partial<Omit<TreeMapConfigFormState, 'type'>>,
  mergedBaseConfig: Partial<BaseConfigFormState>,
  contextRef: string
): ContextViewFormState {
  return {
    system_name: '',
    name: { default: '' },
    description: { default: '' },
    contextRef,
    viewType: 'treemap',
    baseConfig: { ...mergedBaseConfig },
    viewConfig: {
      type: 'treemap',
      ...mergedViewConfig,
    },
  };
}

/**
 * Map a ContextViewRecord (snake_case DB shape) to ContextViewFormState.
 * Injects the type discriminator into viewConfig from view_type.
 */
export function viewRecordToViewForm(record: ContextViewRecord): ContextViewFormState {
  return {
    system_name: record.system_name,
    name: record.name,
    description: record.description ?? { default: '' },
    contextRef: record.context_ref,
    viewType: record.view_type as ContextViewFormState['viewType'],
    baseConfig: record.base_config as BaseConfigFormState,
    viewConfig: {
      type: record.view_type,
      ...(record.view_config as Record<string, unknown>),
    } as ContextViewFormState['viewConfig'],
    listed: record.listed,
  };
}

/**
 * Map a ContextViewFormState to a CreateContextViewPayload (camelCase API shape).
 * Auto-generates systemName from name if system_name is empty.
 */
export function viewFormToCreatePayload(
  form: ContextViewFormState
): CreateContextViewPayload {
  return {
    systemName: form.system_name || generateViewSystemName(form.name),
    name: form.name,
    description: form.description?.default ? form.description : undefined,
    contextRef: form.contextRef,
    viewType: form.viewType,
    baseConfig: form.baseConfig,
    viewConfig: form.viewConfig as unknown as Record<string, unknown>,
  };
}

/**
 * The `base_config` keys that hold a view's SHARED filters. Each is authored only
 * by `useLevelFilters.commitPendingSharedFilters` — `levelFilters` per level,
 * `globalFilters` for the view-wide scope (Atlas column filters).
 */
const SHARED_FILTER_KEYS = ['levelFilters', 'globalFilters'] as const;

/**
 * Map a ContextViewFormState to an UpdateContextViewPayload (camelCase API shape).
 *
 * The shared filter keys (`SHARED_FILTER_KEYS`) never come from the form
 * (EAINT-789). The view editor has no filter-authoring UI — it loads
 * `base_config` when the overlay opens and writes it back verbatim on save — so
 * any shared-filter commit that landed in between
 * (`useLevelFilters.commitPendingSharedFilters`) would be silently reverted by
 * that stale snapshot. They come instead from `storedBaseConfig`, which the
 * caller reads back from the server immediately before the PUT.
 *
 * They are CARRIED, not omitted: `updateContextView` REPLACES `base_config`
 * wholesale (`contextViewHelpers.updateContextView` — there is no
 * `jsonb_deep_merge` on that route; that is `putContextViewState`, the personal
 * overlay), so omitting a key does not leave the stored value untouched, it
 * deletes those shared filters on the view for everyone. The re-read keeps
 * `commitPendingSharedFilters` the only path that AUTHORS shared filters — and
 * so the only one that re-derives edit permission at commit time — while this
 * path only passes the current values through.
 *
 * Create is unaffected: a new view has no stored filters to clobber, and seeding
 * one from the current rendering is intentional.
 */
export function viewFormToUpdatePayload(
  form: ContextViewFormState,
  storedBaseConfig?: Partial<BaseViewConfig>
): UpdateContextViewPayload {
  const baseConfig: Record<string, unknown> = { ...form.baseConfig };
  for (const key of SHARED_FILTER_KEYS) {
    delete baseConfig[key];
    const stored = storedBaseConfig?.[key];
    if (stored !== undefined) baseConfig[key] = stored;
  }

  return {
    name: form.name,
    description: form.description?.default ? form.description : undefined,
    contextRef: form.contextRef,
    viewType: form.viewType,
    baseConfig: baseConfig as UpdateContextViewPayload['baseConfig'],
    viewConfig: form.viewConfig as unknown as Record<string, unknown>,
  };
}
